import type { EventOption, InteractionEvent } from "../types";
import { categoryOf, entrySteps, isDelayOption, stepNameKey } from "./activityFlow";

/**
 * Button order: which steps get a quick-tap button at each point of an
 * activity, and in what order. The order lives in the flow itself — a step's
 * `next` list, and for the very first tap the order of the steps that can
 * start the activity — so the web app and the watch read the same thing.
 */

/** Visits to a place before its own history is trusted over its type's. */
export const MIN_HISTORY_VISITS = 3;

/**
 * Marks an edit that renamed, added or removed steps rather than reordering
 * buttons: those can't be carried onto another list by name.
 */
export const STRUCTURE_EDIT = "__structure";

/** The point before anything is logged, when the start steps are offered. */
export const START_STEP = "__start";

/** The steps offered at a point, in order: what the buttons will be. */
export function stepChoices(options: EventOption[], stepId: string): string[] {
  if (stepId === START_STEP) return entrySteps(options).map((o) => o.id);
  return options.find((o) => o.id === stepId)?.next ?? [];
}

/** Steps that could be offered at a point but aren't: they stay under More…. */
export function hiddenChoices(options: EventOption[], stepId: string): EventOption[] {
  const shown = new Set(stepChoices(options, stepId));
  return options.filter((o) => o.id !== stepId && !shown.has(o.id) && !isDelayOption(o));
}

/**
 * Set what's offered at a point, in order. For a step that's its `next`; at
 * the start it's which steps carry `start`, and their order in the list (the
 * start steps swap places among themselves, so the rest of the list keeps
 * its order).
 */
export function setStepChoices(options: EventOption[], stepId: string, ordered: string[]): EventOption[] {
  if (stepId !== START_STEP) {
    return options.map((o) => (o.id === stepId ? { ...o, next: [...ordered] } : o));
  }
  const chosen = new Set(ordered);
  const flagged = options.map((o) => {
    if (chosen.has(o.id)) return o.start ? o : { ...o, start: true };
    if (!o.start) return o;
    const rest = { ...o };
    delete rest.start;
    return rest;
  });
  // The chosen steps trade places among the slots they hold, so every other
  // step keeps its place in the list.
  const byId = new Map(flagged.map((o) => [o.id, o] as const));
  const slots = flagged.flatMap((o, i) => (chosen.has(o.id) ? [i] : []));
  const result = [...flagged];
  ordered.forEach((id, n) => {
    result[slots[n]!] = byId.get(id)!;
  });
  return result;
}

/** Find a step in another list of the same steps, by name. */
function idByName(options: EventOption[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const o of options) {
    const k = stepNameKey(o.displayName);
    if (!map.has(k)) map.set(k, o.id);
  }
  return map;
}

/**
 * Carry the button order at some points from one list onto another list of
 * the same steps, matched by name: a place's order onto its type's list.
 * Points and steps the other list doesn't have are left alone or dropped.
 */
export function copyStepOrder(
  from: EventOption[],
  to: EventOption[],
  stepIds: Iterable<string>,
): EventOption[] {
  const fromById = new Map(from.map((o) => [o.id, o] as const));
  const toIds = idByName(to);
  const translate = (ids: string[]) =>
    ids
      .map((id) => fromById.get(id))
      .map((o) => (o ? toIds.get(stepNameKey(o.displayName)) : undefined))
      .filter((id): id is string => !!id);
  let result = to;
  for (const stepId of stepIds) {
    const ordered = translate(stepChoices(from, stepId));
    if (stepId === START_STEP) {
      result = setStepChoices(result, START_STEP, ordered);
      continue;
    }
    const step = fromById.get(stepId);
    const target = step ? toIds.get(stepNameKey(step.displayName)) : undefined;
    if (target) result = setStepChoices(result, target, ordered);
  }
  return result;
}

/** Whether each edited point can be found, by name, in another list. */
export function canCopyStepOrder(from: EventOption[], to: EventOption[], stepIds: Iterable<string>): boolean {
  const toIds = idByName(to);
  for (const stepId of stepIds) {
    if (stepId === START_STEP) continue;
    const step = from.find((o) => o.id === stepId);
    if (!step || !toIds.has(stepNameKey(step.displayName))) return false;
  }
  return true;
}

/** How often each step followed each point, keyed `from → to → count`. */
export type StepCounts = Map<string, Map<string, number>>;

/**
 * Count what actually followed what in past activities. Logged events are
 * matched to `options` by their label (by event type when there's none, as the
 * watch writes them); delays and ad-hoc notes are skipped over, and a step
 * logged twice in a row counts once.
 */
export function countStepTransitions(options: EventOption[], activities: InteractionEvent[][]): StepCounts {
  const byName = idByName(options);
  const byId = new Map(options.map((o) => [o.id, o] as const));
  const resolve = (e: InteractionEvent): string | undefined => {
    if (e.customLabel && e.roundIndex == null) return byName.get(stepNameKey(e.customLabel));
    return options.find((o) => o.eventType === e.eventType)?.id;
  };
  const counts: StepCounts = new Map();
  const bump = (from: string, to: string) => {
    let row = counts.get(from);
    if (!row) counts.set(from, (row = new Map()));
    row.set(to, (row.get(to) ?? 0) + 1);
  };
  for (const events of activities) {
    const ms = (e: InteractionEvent) => e.eventTimestamp?.toMillis?.() ?? 0;
    const ordered = [...events].sort((a, b) => ms(a) - ms(b));
    let prev = START_STEP;
    for (const e of ordered) {
      const id = resolve(e);
      const opt = id ? byId.get(id) : undefined;
      if (!opt || isDelayOption(opt) || id === prev) continue;
      bump(prev, id!);
      prev = id!;
    }
  }
  return counts;
}

/** How many times a point was passed through in the counted history. */
export function timesSeen(counts: StepCounts, stepId: string): number {
  let n = 0;
  for (const c of counts.get(stepId)?.values() ?? []) n += c;
  return n;
}

/**
 * The order history suggests at a point: what you took most often first, the
 * rest behind in today's order. It only reorders what's already offered: a
 * step that once followed this one (an Exit Ride after Entered Line, because
 * On Ride was never tapped) is more often a missed tap than a new path.
 */
export function suggestStepOrder(options: EventOption[], counts: StepCounts, stepId: string): string[] {
  const current = stepChoices(options, stepId);
  const row = counts.get(stepId) ?? new Map<string, number>();
  const rank = (id: string) => row.get(id) ?? 0;
  return current
    .map((id, i) => ({ id, i }))
    .sort((a, b) => rank(b.id) - rank(a.id) || a.i - b.i)
    .map((x) => x.id);
}

/**
 * Add a new step right after another one, already wired in: the step before
 * it gets a button for it (last, so the order you set isn't disturbed), and it
 * leads on to wherever that step led, unless it ends the activity. Added at
 * the start, it becomes the first button and leads on to the old start steps.
 */
export function insertStepAfter(options: EventOption[], step: EventOption, afterId: string): EventOption[] {
  const ends = categoryOf(step) === "END";
  if (afterId === START_STEP) {
    const entries = stepChoices(options, START_STEP);
    const added: EventOption = { ...step, next: ends ? [] : entries };
    return setStepChoices([added, ...options], START_STEP, [added.id, ...entries]);
  }
  const at = options.findIndex((o) => o.id === afterId);
  if (at < 0) return [...options, { ...step, next: ends ? [] : step.next ?? [] }];
  const before = options[at]!;
  const added: EventOption = { ...step, next: ends ? [] : [...(before.next ?? [])] };
  const result = options.map((o) => (o.id === afterId ? { ...o, next: [...(o.next ?? []), added.id] } : o));
  result.splice(at + 1, 0, added);
  return result;
}
